/**
 * @fileoverview `buildDiscoverQueryArgs` — single source of truth for the
 * `DiscoverEventsQueryArgs` shape produced from `DiscoverParams + CityCode`.
 *
 * Why this lives here (and not inline in the consumer components):
 *
 * Cold load of `/[city]/discover` used to fire two `useDiscoverEventsQuery`
 * subscriptions (one from `DiscoverResultsBarConnected`, one from
 * `DiscoverListView`) and a redundant SSR `/events/nearby` fetch (API-1415
 * audit). RTK Query dedupes the two client subscriptions because the args
 * object structurally matches between them — but ONLY because both call
 * sites build the args identically. Moving the construction into a shared
 * library function makes that contract explicit and lets the server-side
 * pre-fetch path (also wired in API-1415) produce the same key, so the
 * server response seeds the cache instead of triggering a third request.
 *
 * Mirrors `DiscoverListView.buildQueryArgs` exactly. The list view continues
 * to re-export this for its own `__tests__` so the contract assertions in
 * `DiscoverListView.test.tsx` still resolve.
 *
 * @since API-1415 (Discover · de-duplicate SSR + client `/events/discover` fetches)
 */
import type { CityCode } from '@loci/city-editions';
import type { DiscoverEventsQueryArgs } from '@loci/api';

import { getCityAnchor } from '@/lib/discover/cityAnchor';
import { mapDateKeyword } from '@/lib/discover/dateKeywordMap';
import { mapPricePresetToWire } from '@/lib/discover/pricePreset';
import type { DiscoverParams } from '@/lib/discover/url-state';

/** Backend cap on per-page results — kept aligned with the list view's PAGE_SIZE. */
export const DISCOVER_PAGE_SIZE = 50;

/**
 * Translate URL-state `DiscoverParams` + active `CityCode` into the wire-format
 * args RTK Query consumes. Always injects the city slug (`city`, API-1821)
 * and the geo anchor (`lat`/`lng`/`radiusKm`) so the backend scopes results to
 * the active city. Runs
 * `params.date` through `mapDateKeyword` so UI keywords (`tonight`, `weekend`)
 * become backend keywords the Zod schema accepts.
 *
 * The optional `cursor` argument advances a paginated fetch — keep it `null`
 * for the cold-load / SSR path so the cache key matches the client's first
 * subscription (which always starts with `cursor: null` before "Load more").
 *
 * The optional `today` argument is forwarded to `mapDateKeyword`. Keyword
 * windows (`next7`, `next30`) are resolved on the server (API-1814), so it
 * no longer changes the wire `date`. Production callers should accept the
 * default.
 *
 * The optional `userId` argument (WEB-450) threads the connected viewer's id
 * into the RTK Query cache key so authenticated and anonymous queries land on
 * separate cache entries. It is added to the returned args ONLY when present,
 * so an anonymous call (`userId` omitted / `undefined`) produces args that are
 * byte-identical to the pre-WEB-450 shape — the anonymous single-fetch invariant
 * and every existing cache-key test are unaffected. The connected viewer id must
 * be threaded identically to EVERY discover subscriber (via the shared
 * `useDiscoverEventArgs` hook + `DiscoverViewerIdProvider`) so the mounted
 * subscribers still coalesce onto one cache key. Note it is a cache-key
 * discriminator only — never sent on the wire (see `DiscoverEventsQueryArgs.userId`).
 */
export function buildDiscoverQueryArgs(
  params: DiscoverParams,
  cityCode: CityCode,
  cursor: string | null = null,
  today: Date = new Date(),
  userId?: string,
): DiscoverEventsQueryArgs {
  const anchor = getCityAnchor(cityCode);
  // Geo center + radius precedence (FRE-1103, retired step removed WEB-657):
  //   1. `params.viewport` — an EXPLICIT "Search this area" redo. Overrides the
  //      anchor center so the refetch is scoped to what the user is looking at.
  //   2. the city anchor default (WEB-263 contract).
  // The viewport key only appears after a deliberate redo click — never on
  // cold load — so when it is absent the args below are byte-identical to the
  // pre-FRE-1103 shape and the cold-load single-fetch invariant holds.
  //
  // WEB-657 (DPL-07): `params.radius_km` no longer participates in this
  // precedence chain. The rail's radius slider that used to write it is
  // retired — the query is always centered on the city anchor (or an explicit
  // map viewport redo), never on a viewer-implied personal radius. The
  // `radius_km` field itself stays defined in `DiscoverParams` / url-state.ts
  // because a live cross-feature deep link (Up Next's "near me" toggle,
  // `lib/home/upNextQueryArgs.ts` → `buildUpNextSeeAllHref`) still serializes
  // it into a `/discover?radius_km=…` URL; that link keeps working (no error,
  // no dangling reference), it just no longer narrows the destination query —
  // an intentional consequence of "distance lives on the row and in the sort."
  const center =
    params.viewport !== undefined
      ? { lat: params.viewport.lat, lng: params.viewport.lng }
      : { lat: anchor.lat, lng: anchor.lng };
  const radiusKm = params.viewport?.radiusKm ?? anchor.radiusKm;
  // INVARIANT: single /events/discover fetch per cold load
  // This object IS the RTK Query cache key. SSR prefetch (discover/page.tsx)
  // and every client subscription must build it identically here so RTK Query
  // dedupes them into one network request. See CLAUDE.md "Discover surface"
  // and the cold-mount test (DiscoverTemplate.cold-mount.test.tsx).
  const args: DiscoverEventsQueryArgs = {
    // API-1821 — every Discovery request names its city explicitly. The
    // backend binds the request (neighborhood scope, the city's own radius
    // when no viewport) to it; the `lat`/`lng` below stay the anchor (the city
    // centroid, or a "Search this area" viewport), so the response is the same
    // and the SSR prefetch and every client subscription still share this key.
    city: cityCode.toLowerCase(),
    lat: center.lat,
    lng: center.lng,
    radiusKm,
    category: params.category,
    neighborhood: params.neighborhood,
    // WEB-766: `view` is informational on the wire and does not change the
    // response shape (`DiscoverEventsQueryArgs.view`). Putting it on the args
    // object made List↔Map toggles a new RTK Query cache key, so the
    // always-mounted results bar aborted the in-flight `/events/discover`
    // request and fired a second cross-origin fetch. The abort surfaces in
    // DevTools as a CORS / "Connection Error" toast and the new key misses
    // the SSR seed — empty list + 0 facets. Omit the key entirely (same
    // pattern as `followedOnly: false`) so both views share one cache entry.
    sort: params.sort,
    limit: DISCOVER_PAGE_SIZE,
  };
  const mappedDate = mapDateKeyword(params.date, today);
  if (mappedDate) args.date = mappedDate;
  if (params.q) args.q = params.q;
  // WEB-656 / DPL-06: map the price PRESET to explicit priceMin/priceMax wire
  // bounds — never forward `params.price` raw. The backend has no preset
  // vocabulary (`discoverQuerySchema` only accepts numeric priceMin/priceMax);
  // a raw preset slug would be silently dropped the same way the pre-WEB-658
  // `time=` filter was. `mapPricePresetToWire` returns undefined for the
  // cleared state, so an unfiltered cold load stays byte-identical (no
  // priceMin/priceMax keys at all) — see `lib/discover/pricePreset.ts` for how
  // unlisted prices are handled.
  const priceWire = mapPricePresetToWire(params.price);
  if (priceWire) {
    args.priceMin = priceWire.priceMin;
    if (priceWire.priceMax !== undefined) args.priceMax = priceWire.priceMax;
    // WEB-1230 (R6): the "+N not listed" toggle. Only with a preset and only
    // when ON, so the off state adds no key to the cache key.
    if (params.priceUnlisted === true) args.includeUnpriced = true;
  }
  // FRE-1145: forward `venue` only when present, so a cold load with no venue
  // selected stays byte-identical to the pre-FRE-1145 shape (single-fetch
  // invariant).
  if (params.venue?.length) args.venue = params.venue;
  // WEB-662 / DPL-12: forward the "Only who I follow" toggle only when ON,
  // mirroring the `venue` guard above. The off state must NOT
  // materialize a `followedOnly: false` key — RTK Query hashes the whole args
  // object, so an explicit `false` would produce a different cache key from a
  // never-toggled cold load and split one subscription into two requests, even
  // though both would serialize to the same URL. Absence is the off state, end
  // to end (url-state → args → wire).
  if (params.followedOnly === true) args.followedOnly = true;
  if (cursor !== null && cursor.length > 0) args.cursor = cursor;
  // WEB-450: added only when present so anonymous args stay byte-identical (see jsdoc).
  if (userId) args.userId = userId;
  return args;
}
